import type { ConversionTracked } from "@/hooks/eventing/types/core.conversions.ConversionTracked.v2";
import { TagularAction, TagularCategory } from "@/types/Tagular";
import type { CartItem } from "@/utils/cartUtils";
import {
  TagularVariant,
  translateVariant,
} from "@/utils/tagular/dataTranslationMatrices";

const getVariant = (productType?: string) =>
  productType
    ? (translateVariant(productType) ?? TagularVariant.Courses)
    : TagularVariant.Courses;

/**
 * Creates product view event data for a cart item
 */
export const createProductViewEventData = (
  item: CartItem,
  index: number,
  cartBrand?: string | null,
  correlationId?: string,
) => ({
  product: {
    brand: cartBrand ?? item.brand ?? "edX",
    category: item.type ?? "course",
    formatSubtype: "modal",
    formatType: "abandoned-cart",
    location: "abandoned-cart-modal",
    name: item.courseName,
    position: index + 1,
    productId: item.id,
    variant: getVariant(item.type),
  },
  viewCorrelationId: correlationId || "abandoned-cart-modal",
});

/**
 * Creates element viewed event data for a cart item
 */
export const createElementViewEventData = (
  item: CartItem,
  index: number,
  correlationId: string,
) => ({
  webElement: {
    name: `cart-product-${item.id}`,
    htmlId: `item-${item.id}`,
    elementType: "listitem",
    location: "abandoned-cart-modal",
    position: (index + 1).toString(),
    text: item.courseName,
    variant: getVariant(item.type),
  },
  correlationId,
});

/**
 * Creates conversion event data for cart checkout
 */
export const createConversionEventData = (
  items: CartItem[],
  cartBrand?: string | null,
  correlationId?: string,
  customName = "cart_checkout",
): ConversionTracked => ({
  correlation: {
    id: correlationId || "",
  },
  metadata: {
    customName,
    action: TagularAction.STARTED,
    category: TagularCategory.ENROLLMENT,
    timestamp: new Date().toISOString(),
    productList: items.map((item) => ({
      brand: cartBrand ?? item.brand ?? "edX",
      category: item.type ?? "course",
      variant: getVariant(item.type),
      name: item.courseName,
      id: item.courseKey,
      externalId: item.courseRunKey,
    })),
  },
});

/**
 * Creates product view event data for cart intent match (cart suggestions)
 */
export const createCartIntentProductViewEventData = (
  item: CartItem,
  index: number,
  cartBrand?: string | null,
  location: string = "nav-bar",
  formatSubtype: string = "cart",
  correlationId?: string,
) => ({
  product: {
    brand: cartBrand ?? item.brand ?? "edX",
    category: item.type ?? "course",
    formatSubtype,
    formatType: "cart-intent-match",
    location,
    name: item.courseName,
    position: index + 1,
    productId: item.id,
    variant: getVariant(item.type),
  },
  viewCorrelationId: correlationId || "cart-intent-match",
});

/**
 * Creates conversion event data for cart intent match
 */
export const createCartIntentConversionEventData = (
  items: CartItem[],
  cartBrand?: string | null,
  correlationId?: string,
): ConversionTracked => ({
  correlation: {
    id: correlationId || "",
  },
  metadata: {
    action: TagularAction.STARTED,
    category: TagularCategory.ENROLLMENT,
    timestamp: new Date().toISOString(),
    productList: items.map((item) => ({
      brand: cartBrand ?? item.brand ?? "edX",
      category: item.type ?? "course",
      variant: getVariant(item.type),
      name: item.courseName,
      id: item.courseKey,
      externalId: item.courseRunKey,
    })),
  },
});

/**
 * Creates element viewed event data for banner
 */
export const createBannerElementViewEventData = (
  location: string,
  productName: string,
  correlationId?: string,
) => ({
  webElement: {
    elementType: "banner",
    htmlId: "banner",
    location,
    name: "abandoned-cart-banner",
    text: `Still interested in ${productName}?`,
  },
  correlationId,
});

/**
 * Creates element clicked event data for banner checkout button
 */
export const createBannerCheckoutClickEventData = (
  location: string,
  productName: string,
  correlationId?: string,
) => ({
  webElement: {
    elementType: "button",
    htmlId: "banner",
    location,
    name: "abandoned-cart-banner",
    text: `Still interested in ${productName}?`,
  },
  correlationId,
});

/**
 * Creates product view event data for banner products
 */
export const createBannerProductViewEventData = (
  item: CartItem,
  index: number,
  location?: string,
  cartBrand?: string | null,
  correlationId?: string,
) => ({
  product: {
    brand: cartBrand ?? item.brand ?? "edX",
    category: item.type ?? "course",
    formatSubtype: "banner",
    formatType: "cart-intent-match",
    location,
    name: item.courseName,
    position: index + 1,
    productId: item.id,
    variant: getVariant(item.type),
  },
  viewCorrelationId: correlationId || "abandoned-cart-banner",
});

/**
 * Creates conversion event data for banner
 */
export const createBannerConversionEventData = (
  items: CartItem[],
  cartBrand?: string | null,
  correlationId?: string,
): ConversionTracked => ({
  correlation: {
    id: correlationId || "",
  },
  metadata: {
    action: TagularAction.STARTED,
    category: TagularCategory.ENROLLMENT,
    timestamp: new Date().toISOString(),
    productList: items.map((item) => ({
      brand: cartBrand ?? item.brand ?? "edX",
      category: item.type ?? "course",
      variant: getVariant(item.type),
      name: item.courseName,
      id: item.courseKey,
      externalId: item.courseRunKey,
    })),
  },
});
